/**
 * Azure AD Authentication Service
 * Handles Microsoft Azure Active Directory authentication
 */
import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { getAuthConfig } from '@core/config/auth.config';

class AzureADService {
  private msalInstance: PublicClientApplication | null = null;
  private lastAuthResult: AuthenticationResult | null = null;
  private initPromise: Promise<void> | null = null;
  private redirectResponse: AuthenticationResult | null = null;

  /**
   * Initialize MSAL instance and handle redirect
   */
  async initialize(): Promise<void> {
    if (this.msalInstance) return;
    if (this.initPromise) {
      await this.initPromise;
      return;
    }

    this.initPromise = (async () => {
      const authConfig = getAuthConfig();
      
      if (!authConfig.isValid || !authConfig.azureAd.clientId || !authConfig.azureAd.authority) {
        throw new Error('Azure AD configuration is missing');
      }

      this.msalInstance = new PublicClientApplication({
        auth: {
          clientId: authConfig.azureAd.clientId,
          authority: authConfig.azureAd.authority,
          redirectUri: authConfig.azureAd.redirectUri,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        },
      });

      await this.msalInstance.initialize();
      
      // Handle redirect immediately after initialization (required by MSAL)
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.msalInstance.setActiveAccount(response.account);
        this.lastAuthResult = response;
        this.redirectResponse = response;
        console.log('[Azure AD] Login successful. Token received:', {
          idToken: response.idToken,
          accessToken: response.accessToken,
          account: response.account?.username,
          expiresOn: response.expiresOn,
        });
      } else {
        this.redirectResponse = null;
      }
    })();

    await this.initPromise;
  }

  /**
   * Get redirect response (already handled during initialization)
   */
  getRedirectResponse(): AuthenticationResult | null {
    return this.redirectResponse;
  }

  async loginWithPopup(): Promise<AuthenticationResult> {
    await this.initialize();
    if (!this.msalInstance) throw new Error('MSAL not initialized');

    const authConfig = getAuthConfig();
    const account = this.getAccount();
    
    // Add login hint if there's a previous account to avoid account selection
    const loginRequest: any = {
      scopes: authConfig.azureAd.scopes || ['User.Read'],
    };
    
    if (account) {
      loginRequest.loginHint = account.username;
    }
    
    const response = await this.msalInstance.loginPopup(loginRequest);

    this.msalInstance.setActiveAccount(response.account);
    this.lastAuthResult = response;
    return response;
  }

  async loginWithRedirect(): Promise<void> {
    await this.initialize();
    if (!this.msalInstance) throw new Error('MSAL not initialized');

    const authConfig = getAuthConfig();
    await this.msalInstance.loginRedirect({
      scopes: authConfig.azureAd.scopes || ['User.Read'],
    });
  }

  async getGraphAccessToken(): Promise<string> {
    await this.initialize();
    if (!this.msalInstance) throw new Error('MSAL not initialized');

    const account = this.getAccount();
    if (!account) throw new Error('No active account');

    const graphScopes = ['https://graph.microsoft.com/User.Read'];
    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: graphScopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await this.msalInstance.acquireTokenPopup({ scopes: graphScopes });
        return response.accessToken;
      }
      throw error;
    }
  }

  async getAccessToken(): Promise<string> {
    await this.initialize();
    if (!this.msalInstance) throw new Error('MSAL not initialized');

    const account = this.getAccount();
    if (!account) throw new Error('No active account');

    try {
      const authConfig = getAuthConfig();
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: authConfig.azureAd.scopes || ['User.Read'],
        account,
      });
      this.lastAuthResult = response;
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const authConfig = getAuthConfig();
        const response = await this.msalInstance.acquireTokenPopup({
          scopes: authConfig.azureAd.scopes || ['User.Read'],
        });
        this.lastAuthResult = response;
        return response.accessToken;
      }
      throw error;
    }
  }

  /**
   * Get ID token from last authentication result
   * ID token is required for SSO endpoint
   */
  getIdToken(): string | null {
    return this.lastAuthResult?.idToken || null;
  }

  async getIdTokenAsync(): Promise<string> {
    await this.initialize();
    if (!this.msalInstance) throw new Error('MSAL not initialized');

    if (this.lastAuthResult?.idToken) {
      return this.lastAuthResult.idToken;
    }

    const account = this.getAccount();
    if (!account) throw new Error('No active account');

    try {
      const authConfig = getAuthConfig();
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: authConfig.azureAd.scopes || ['User.Read'],
        account,
      });
      this.lastAuthResult = response;
      return response.idToken || '';
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const authConfig = getAuthConfig();
        const response = await this.msalInstance.acquireTokenPopup({
          scopes: authConfig.azureAd.scopes || ['User.Read'],
        });
        this.lastAuthResult = response;
        return response.idToken || '';
      }
      throw error;
    }
  }

  /**
   * Get current account
   */
  getAccount(): AccountInfo | null {
    if (!this.msalInstance) return null;

    const accounts = this.msalInstance.getAllAccounts();
    return accounts.length > 0 ? accounts[0] : null;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null;
  }

  async logout(): Promise<void> {
    // Clear local state first
    this.lastAuthResult = null;
    this.redirectResponse = null;
    
    if (!this.msalInstance) {
      // If not initialized, just clear session storage
      sessionStorage.clear();
      return;
    }

    // Redirect to /login after logout (not back to app root)
    const postLogoutRedirectUri = `${window.location.origin}/login`;
    
    // Clear MSAL cache and redirect to login page
    await this.msalInstance.logoutRedirect({
      postLogoutRedirectUri,
    });
  }

  /**
   * Get user info
   */
  getUserInfo(): any {
    const account = this.getAccount();
    if (!account) return null;

    return {
      id: account.localAccountId,
      name: account.name || '',
      email: account.username || '',
      roles: [], // Get from token claims if needed
    };
  }
}

export const azureADService = new AzureADService();
